/**
 * WorkspacePlaybooksDialog - Modal de gerenciamento de Playbooks do workspace
 *
 * Wrapper de Dialog que reaproveita o componente WorkspacePlaybooksTab
 * (zero duplicação de lógica). Substitui o antigo posicionamento como aba
 * dentro do collapsible do workspace.
 *
 * Layout responsivo padrão:
 *  - Desktop: max-w-6xl, ~90dvh, scroll interno.
 *  - Mobile: tela cheia (w-screen h-[100dvh]).
 *
 * @version 1.0.0
 * RELEASE: 2026-05-08 v1.0.0 - Criação inicial (refator UI: Tab → Modal)
 */

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Zap } from 'lucide-react';
import { WorkspacePlaybooksTab } from './WorkspacePlaybooksTab';

interface WorkspacePipelinePhase {
  id: string;
  name: string;
  color: string | null;
  sort_order: number;
  is_won: boolean;
  is_lost: boolean;
}

interface WorkspacePipeline {
  id: string;
  name: string;
  phases?: WorkspacePipelinePhase[];
}

interface WorkspacePlaybooksDialogProps {
  /** Workspace selecionado (ou null para fechar) */
  workspace: {
    id: string;
    name: string;
    playbook_mode_enabled: boolean;
  } | null;
  /** Tenant atual (necessário para o WorkspacePlaybooksTab) */
  tenantId: string | null;
  /** Pipelines/fases do workspace para mapear bindings */
  pipelines: WorkspacePipeline[];
  /** Controle de abertura */
  open: boolean;
  /** Callback de fechamento */
  onOpenChange: (open: boolean) => void;
}

export function WorkspacePlaybooksDialog({
  workspace,
  tenantId,
  pipelines,
  open,
  onOpenChange,
}: WorkspacePlaybooksDialogProps) {
  if (!workspace || !tenantId) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl w-screen h-[100dvh] sm:w-[95vw] sm:h-[90dvh] flex flex-col p-0 gap-0 sm:rounded-lg">
        {/* Header fixo */}
        <DialogHeader className="px-6 py-4 border-b shrink-0">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Zap className="h-5 w-5 text-primary" />
            Playbook de Vendas — {workspace.name}
          </DialogTitle>
          <DialogDescription>
            Ative o modo Playbook e configure regras de disparo automático de
            cadências de atividades para os negócios deste workspace.
          </DialogDescription>
        </DialogHeader>

        {/* Body com scroll */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <WorkspacePlaybooksTab
            workspaceId={workspace.id}
            tenantId={tenantId}
            playbookModeEnabled={workspace.playbook_mode_enabled}
            pipelines={pipelines}
          />
        </div>

        {/* Footer fixo */}
        <DialogFooter className="px-6 py-3 border-t shrink-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
